import { ChangeDetectorRef, Injectable } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { doc, DocumentData, DocumentSnapshot, Firestore, getDoc } from "@angular/fire/firestore";
import { MatStepper } from "@angular/material/stepper";
import { DomSanitizer } from "@angular/platform-browser";
import { Data, Router } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { CatchErrorService } from "@core/catch-error.service";
import { CelebrationAnimationService } from "@core/celebration-animation.service";
import { LoadingService } from "@core/loading.service";
import { ToastFacadeService } from "@core/toast-facade.service";
import { UtilityService } from "@core/utility.service";
import { getAuth } from "firebase/auth";
import { from, Subject, throwError } from "rxjs";
import { catchError, retry, shareReplay, switchMap, take, takeUntil } from "rxjs/operators";
import { WalletService } from "src/app/pages/account/account-wallet/wallet.services";
import { CommonService } from "src/app/services/common/common.service";
import { MixpanelService } from "src/app/services/mixpanel.service";
import { PaymentService } from "src/app/services/payment.service";
import { SalesJourneyIdentityService } from "src/app/services/sales-journey-identity.service";
import { VoucherService } from "src/app/services/vouchers.service";
import { environment } from "src/environments/environment";
import { v4 as uuidv4 } from "uuid";
import { StoreCheckoutFlowService } from "../../services/store-checkout-flow.service";
import { StoreRepositoryService } from "../../store-repository.service";
import { AddCardComponent } from "../add-card.component";

@Injectable()
export class CheckoutPaymentService {
  private unsubscribeStatusFromCP$ = new Subject<void>();
  private unsubscribeStatusFromPayment$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private paymentService: PaymentService,
    private commonService: CommonService,
    private catchService: CatchErrorService,
    private celebrationAnimationService: CelebrationAnimationService,
    private sanitizer: DomSanitizer,
    private angularFireAuth: AngularFireAuth,
    private router: Router,
    private loadingService: LoadingService,
    private voucherService: VoucherService,
    private storeService: StoreRepositoryService,
    private mixPanelService: MixpanelService,
    private firestore: Firestore,
    private utilityService: UtilityService,
    private walletService: WalletService,
    private toastFacade: ToastFacadeService,
    private storeCheckoutFlowService: StoreCheckoutFlowService,
    private salesJourneyIdentityService: SalesJourneyIdentityService,
  ) {}

  /**
   * Wait for Checkout.com Flow payment confirmation via Firestore.
   */
  handleFlowPaymentConfirmation(componentContext: AddCardComponent, cbRef: ChangeDetectorRef): void {
    componentContext.addNewCardInProgress = true;
    componentContext.showCheckOutComSpinner = true;
    this.celebrationAnimationService.setCreditTopupStatus(true);
    cbRef.detectChanges();
    this.loadingService.present();

    this.commonService
      .getUid("handleFlowPaymentConfirmation")
      .pipe(take(1))
      .subscribe((uid: string) => {
        if (uid) {
          this.paymentService.checkoutFlowUserId$.next(uid);
        }

        componentContext.getCheckOutComResponseSubscription = this.storeCheckoutFlowService.waitForPaymentConfirmation(
          {
            keys: componentContext.keys,
            language: componentContext.language,
            paymentMode: componentContext.paymentMode,
            pathString: componentContext.pathString,
            checkOutComData: componentContext.checkOutComData,
            purchaseDetail$: componentContext.purchaseDetail$,
            userData: componentContext.userData,
            preferredCurrency: componentContext.preferredCurrency,
            selectedDiscountValue: componentContext.selectedDiscountValue,
            updatedPrizeValue: componentContext.updatedPrizeValue,
            isCheckoutComform: componentContext.isCheckoutComform,
            isButtonDisabled: componentContext.isButtonDisabled,
            showCheckOutComSpinner: componentContext.showCheckOutComSpinner,
            errorMessage: componentContext.errorMessage,
            checkoutFlowReady: componentContext.checkoutFlowReady,
            cardTokenizedCalled: componentContext.cardTokenizedCalled,
            addNewCardInProgress: componentContext.addNewCardInProgress,
            getCookie: (name) => componentContext.getCookie(name),
            getCheckOutComResponseSubscription: componentContext.getCheckOutComResponseSubscription,
          },
          {
            onSuccess: (paymentResData) => {
              this.applyCheckoutFlowPaymentSuccess(componentContext, paymentResData, cbRef);
            },
            onFailure: () => this.checkoutPaymentFailedFlow(componentContext),
            onTimeout: () => {
              componentContext.addNewCardInProgress = false;
              componentContext.showCheckOutComSpinner = false;
              componentContext.errorMessage = "Payment confirmation timed out. Please try again.";
              this.loadingService.dismiss();
              this.celebrationAnimationService.setCreditTopupStatus(false);
              cbRef.detectChanges();
            },
          },
        );
      });
  }

  private applyCheckoutFlowPaymentSuccess(
    componentContext: AddCardComponent,
    paymentResData: Data,
    cbRef: ChangeDetectorRef,
  ): void {
    const paymentResponse = paymentResData?.payment_response;
    componentContext.customerPurchaseEncryptedId =
      paymentResData?.customer_purchase_encrypted_id ??
      paymentResData?.payment_request?.customer_purchase?.encrypted_customer_purchase_id;
    componentContext.showCheckOutComSpinner = false;
    componentContext.addNewCardInProgress = false;

    if (!paymentResponse?.payment_status) {
      this.loadingService.dismiss();
      cbRef.detectChanges();
      return;
    }

    if (componentContext.addCardPaymentStatus) {
      this.loadingService.dismiss();
      cbRef.detectChanges();
      return;
    }

    componentContext.cardTokenizedCalled = false;
    componentContext.paymentHold = false;
    componentContext.getCheckOutComResponseSubscription?.unsubscribe();
    componentContext.generateCheckoutTokenSubscription?.unsubscribe();

    if (paymentResponse.payment_status !== "success") {
      this.checkoutPaymentFailedFlow(componentContext);
      cbRef.detectChanges();
      return;
    }

    this.paymentService.emitSyndicatePurchaseComplete();
    if (componentContext.selectedDiscountValue > 0) {
      this.mixPanelService.trackVoucherEvent("voucher_manual_applied", componentContext.selectedDiscountVoucherData);
    } else if (componentContext.discountVouchersData?.length > 0) {
      this.mixPanelService.trackVoucherEvent("voucher_skipped", componentContext.discountVouchersData[0]);
    }

    this.loadingService.dismiss();
    componentContext.stepperIndex = 1;
    componentContext.startCountdown();
    if (componentContext.stepper?.selected) {
      componentContext.stepper.selected.completed = true;
      componentContext.stepper.selected.editable = false;
      componentContext.stepper.next();
    }
    componentContext.timerOn = false;
    this.voucherService.updateDiscountVoucherRedeemedStatus(componentContext.selectedDiscountVoucherData, true);
    componentContext.addCardPaymentStatus = true;
    componentContext.orderSuccessEvent();

    if (!paymentResponse.show_toast) {
      this.authService
        .getConstantsMsg("1092")
        .then((translatedMsg) => {
          const defaultMsg = {
            message: {
              line_1: "Payment has been successfully processed",
              line_2: "",
            },
            style: {
              icon: "tick circle success.png",
              type: "Success",
            },
          };
          this.authService.showToastMessage(translatedMsg, defaultMsg);
        })
        .catch((error) => {
          this.catchService.logError("Error fetching constants message:", error);
        });

      paymentResponse.show_toast = true;

      this.commonService
        .getUid("handleFlowPaymentConfirmation")
        .pipe(take(1))
        .subscribe((uid: string) => {
          const path_id = `JL4LGZDCJv0wpAfFrAPc`;
          const doc_data = { payment_response: paymentResponse };
          const dv = { docId: uid, pathString: componentContext.pathString };
          this.authService
            .getV2CloudFunction("updatefirestoredoc", {
              path_id,
              doc_data,
              dv,
              method: "update",
            })
            .pipe(take(1))
            .subscribe();
        });
    }

    const encryptSyndicateId =
      paymentResponse?.encrypt_syndicate_id ??
      paymentResData?.encrypt_syndicate_id ??
      paymentResData?.api_response?.response?.encrypt_syndicate_id;

    if (encryptSyndicateId) {
      this.paymentService.cashPurchaseAddCardSyndicateId$.next(String(encryptSyndicateId));
    }

    const purchaseDetail = componentContext.purchaseDetail$?.value;
    const isTopUp = purchaseDetail?.isTopUp || purchaseDetail?.page === "topUp";
    cbRef.detectChanges();

    if (isTopUp) {
      this.celebrationAnimationService.setCreditTopupStatus(false);
      this.celebrationAnimationService.trackCelebrations();
      return;
    }

    this.celebrationAnimationService.setCreditTopupStatus(false);
    this.redirectAfterPaymentSuccess(componentContext, encryptSyndicateId);
    this.celebrationAnimationService.trackCelebrations();
    cbRef.detectChanges();
  }

  /**
   * Process card token and initiate payment
   */
  processCardToken(event: Data, componentContext: AddCardComponent, cbRef: ChangeDetectorRef): void {
    componentContext.paymentReason = componentContext.purchaseDetail$.value?.page === "store" ? "offer" : "award";
    if (componentContext.cardTokenizedCalled) return;
    componentContext.cardTokenizedCalled = true;
    componentContext.showCheckOutComSpinner = true;
    this.storeCheckoutFlowService.unmount();
    componentContext.isCheckoutComform = false;
    componentContext.token = event.token;

    const purchaseDetail = componentContext.purchaseDetail$?.value;
    const amt = componentContext.checkOutComData?.prize || purchaseDetail?.prize;
    const msn = componentContext.checkOutComData?.msn || componentContext.userData?.msn;
    const userNumber = msn?.substring(2);
    const auth = getAuth();
    const TP1 = localStorage.getItem("TP1") || "";
    const TP2 = localStorage.getItem("TP2") || "";
    const TP3 = localStorage.getItem("TP3") || "";
    const T_ID = localStorage.getItem("T_ID") || "";
    const voucherDiscount = componentContext.selectedDiscountValue || 0;
    const valuePrize = purchaseDetail?.offers?.price || purchaseDetail?.prize;

    // ✅ Get UID from commonService before building paymentData
    this.commonService
      .getUid("processCardToken")
      .pipe(take(1))
      .subscribe({
        next: (uid: string) => {
          if (!uid) {
            return;
          }
          const paymentData = this.salesJourneyIdentityService.applyIdentityEnvelope(
            {
              token: componentContext.token,
              amount:
                purchaseDetail?.add_on?.length > 0
                  ? (componentContext.updatedPrizeValue + parseFloat(amt)) * (1 - voucherDiscount)
                  : parseFloat(amt) * (1 - voucherDiscount),
              currency: componentContext.preferredCurrency,
              email: componentContext.checkOutComData?.email || msn + "@lottosocial.com",
              name: componentContext.userData?.name,
              phone: msn,
              userId: uid,
              logId: componentContext.pathString,
              msn: {
                country_code: `+${componentContext.checkOutComData?.country_code || componentContext.userData?.country_code}`,
                number: userNumber,
              },
              payment_reason: componentContext.paymentReason,
              offer_id: purchaseDetail?.offers?.offerid || purchaseDetail?.offer_id,
              prosub_id: purchaseDetail?.prosub_id || localStorage.getItem("prosub_id"),
              anonymous_id: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
              tp1: TP1 === "null" ? "" : TP1,
              tp2: TP2 === "null" ? "" : TP2,
              tp3: TP3 === "null" ? "" : TP3,
              t_id: T_ID === "null" ? "" : T_ID,
              Other: purchaseDetail.other
                ? purchaseDetail.other
                : localStorage.getItem("Other")
                  ? localStorage.getItem("Other")
                  : componentContext.getCookie("Other")
                    ? componentContext.getCookie("Other")
                    : "",
              value: valuePrize - valuePrize * voucherDiscount,
              preferred_currency: {
                Value: purchaseDetail.offers?.int_price?.[componentContext.preferredCurrency]
                  ? purchaseDetail.offers.int_price[componentContext.preferredCurrency] -
                    purchaseDetail.offers.int_price[componentContext.preferredCurrency] * voucherDiscount
                  : purchaseDetail?.prize - purchaseDetail?.prize * voucherDiscount,
                currency: componentContext.preferredCurrency,
              },
              p_type: purchaseDetail?.p_type || "21",
              visitorid: 111,
              requestid: uuidv4(),
              product_description: purchaseDetail?.isTopUp ? "Award" : (purchaseDetail?.product_description ?? ""),
              add_on: purchaseDetail?.add_on ?? [],
            },
            {
              userId: auth?.currentUser?.isAnonymous ? null : (uid ?? null),
              anonymousId: auth?.currentUser?.isAnonymous ? auth.currentUser.uid : null,
              // Omit customerId: resolve the actual SQL ID when available.
              msn: msn,
              checkoutAttemptId: componentContext.pathString,
            },
          );

          // ---- Call Checkout Token API ----
          const generateCheckOutToken =
            componentContext.paymentMode === "sandbox"
              ? "generatecheckouttokensbv2"
              : environment.cloudFunctions.generateCheckOutToken;
          componentContext.generateCheckoutTokenSubscription = this.authService
            ?.getV2CloudFunction(generateCheckOutToken, paymentData)
            .pipe(
              retry(3),
              catchError((error) => throwError(error)),
            )
            .subscribe(
              (res: Data) => {
                if (!res) return;
                const responseCode = res?.response_code;

                if (responseCode === "20150" || responseCode === "40101") {
                  this.checkoutPaymentFailedFlow(componentContext);
                  return;
                }

                const otpUrl = res?.redirectLink;
                if (responseCode === "20150" || responseCode === "40101" || !otpUrl) {
                  this.checkoutPaymentFailedFlow(componentContext);
                  return;
                }
                componentContext.checkAndUpdateStylesForIframe();
                componentContext.redirectlink = this.sanitizer.bypassSecurityTrustResourceUrl(otpUrl);
                cbRef.detectChanges();
              },
              (err) => {
                this.catchService.logError("Checkout token error:", err);
              },
            );

          // ---- Listen for Checkout.com response ----
          componentContext.getCheckOutComResponseSubscription = this.paymentService.getCheckOutComResponse$.subscribe(
            (paymentResData: Data) => {
              const paymentResponse = paymentResData?.payment_response;
              componentContext.customerPurchaseEncryptedId = paymentResData?.customer_purchase_encrypted_id;
              componentContext.showCheckOutComSpinner = false;

              if (paymentResponse?.payment_status && !paymentResponse?.show_toast) {
                componentContext.cardTokenizedCalled = false;
                componentContext.paymentHold = false;
                componentContext.getCheckOutComResponseSubscription?.unsubscribe();
                componentContext.generateCheckoutTokenSubscription?.unsubscribe();

                if (paymentResponse?.payment_status === "success") {
                  this.paymentService.emitSyndicatePurchaseComplete();
                  if (componentContext.selectedDiscountValue > 0) {
                    this.mixPanelService.trackVoucherEvent(
                      "voucher_manual_applied",
                      componentContext.selectedDiscountVoucherData,
                    );
                  } else {
                    this.mixPanelService.trackVoucherEvent("voucher_skipped", componentContext.discountVouchersData[0]);
                  }
                  this.loadingService.dismiss();
                  componentContext.stepperIndex = 1;
                  componentContext.startCountdown();
                  componentContext.stepper.selected.completed = true;
                  componentContext.stepper.selected.editable = false;
                  componentContext.timerOn = false;
                  this.voucherService.updateDiscountVoucherRedeemedStatus(
                    componentContext.selectedDiscountVoucherData,
                    true,
                  );
                  componentContext.addCardPaymentStatus = true;
                  componentContext.stepper.next();

                  this.authService
                    .getConstantsMsg("1092")
                    .then((translatedMsg) => {
                      const defaultMsg = {
                        message: {
                          line_1: "Payment has been successfully processed",
                          line_2: "",
                        },
                        style: {
                          icon: "tick circle success.png",
                          type: "Success",
                        },
                      };
                      this.authService.showToastMessage(translatedMsg, defaultMsg);
                    })
                    .catch((error) => {
                      this.catchService.logError("Error fetching constants message:", error);
                      return error;
                    });

                  this.redirectAfterPaymentSuccess(componentContext);
                  paymentResponse.show_toast = true;

                  const path_id = `JL4LGZDCJv0wpAfFrAPc`;
                  const doc_data = { payment_response: paymentResponse };
                  const dv = { docId: uid, pathString: componentContext.pathString };
                  const method = "update";

                  this.authService
                    .getV2CloudFunction("updatefirestoredoc", {
                      path_id,
                      doc_data,
                      dv,
                      method,
                    })
                    .pipe(take(1))
                    .subscribe();
                } else {
                  this.checkoutPaymentFailedFlow(componentContext);
                }
              }
            },
          );
        },
        error: (err) => {
          this.catchService.logError("Error getting UID in processCardToken:", err);
        },
      });
  }

  /**
   * Handle payment completion for new card payment via CP
   */
  afterNewCardPaymentDoneUpdated(stepper: MatStepper, componentContext: AddCardComponent): void {
    if (!componentContext.paymentReceivedFromCP) {
      componentContext.paymentReceivedFromCP = true;
      this.unsubscribeStatusFromCP$ = new Subject<void>();
      this.unsubscribeStatusFromPayment$ = new Subject<void>();
      this.paymentService
        .getCustomerPurchaseDetailsFromSQL(componentContext.currentNdcSignal())
        .pipe(takeUntil(this.unsubscribeStatusFromCP$))
        .subscribe((data: Data) => {
          if (data) {
            const purchaseDoc = data;
            const processed_status = purchaseDoc.processed_status;
            if (processed_status !== undefined) {
              if (processed_status === 2) {
                this.paymentService.emitSyndicatePurchaseComplete();
                this.celebrationAnimationService.setCreditTopupStatus(true);
                componentContext.stepperIndex = 1;
                stepper.selected.completed = true;
                stepper.selected.editable = false;
                componentContext.isPaymentDone = false;
                componentContext.timerOn = false;
                stepper.next();
                const defaultMsg = {
                  message: {
                    line_1: "Payment successful!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle success.png",
                    type: "Success",
                  },
                };
                this.authService.showToastMessage({}, defaultMsg);
                let token = localStorage.getItem("fbToken");
                const encryptSyndicateId = purchaseDoc?.api_response?.response?.encrypt_syndicate_id;
                if (token) {
                  this.manualLoginPrivate(token, componentContext).then(() => {
                    if (encryptSyndicateId) {
                      this.paymentService.cashPurchaseAddCardSyndicateId$.next(encryptSyndicateId);
                    }
                    return;
                  });
                }
                this.redirectAfterPaymentSuccess(componentContext, encryptSyndicateId);
                this.resetPaymentSubscriptions();
              } else if (processed_status === -1) {
                const defaultMsg = {
                  message: {
                    line_1: "Payment failed, please try again!",
                    line_2: "",
                  },
                  style: {
                    icon: "tick circle error.svg",
                    type: "Error",
                  },
                };
                this.authService.showToastMessage({}, defaultMsg);
                componentContext.addNewCardInProgress = false;
                componentContext.isPaymentDone = false;
                componentContext.count = 0;
                componentContext.paymentReceived = false;
                componentContext.paymentReceivedFromCP = false;
                componentContext.timerOn = false;
                this.resetPaymentSubscriptions();
              }
            }
          }
        });
    }
  }

  /**
   * Handle payment completion for new card (main flow)
   */
  afterNewCardPaymentDone(stepper: MatStepper, componentContext: AddCardComponent): void {
    try {
      if (!componentContext.paymentReceived && localStorage.getItem("d")) {
        componentContext.paymentReceived = true;
        componentContext.paymentReceivedFromCP = true;
        const urlData = JSON.parse(localStorage.getItem("d"));
        localStorage.removeItem("d");

        setTimeout(() => {
          this.commonService
            .getUid("afterNewCardPaymentDone")
            .pipe(take(1))
            .subscribe({
              next: (uid: string) => {
                if (!componentContext.pathString || !uid) return;

                try {
                  this.paymentService
                    .checkPaymentStatus(urlData?.id, componentContext.paymentMode)
                    .pipe(take(1), takeUntil(this.unsubscribeStatusFromPayment$))
                    .subscribe(
                      (payment_status) => {
                        this.authService.commonUserCheck(() => {
                          componentContext.payment_status = payment_status;

                          // ✅ Payment succeeded
                          if (
                            payment_status?.result.description.search("succeeded") > -1 ||
                            payment_status?.result.description.search("successfully") > -1
                          ) {
                            this.paymentService.emitSyndicatePurchaseComplete();
                            componentContext.addNewCardInProgress = false;

                            const customOfferToast = localStorage.getItem("custom-offer-toast");
                            if (
                              customOfferToast === "true" &&
                              componentContext.customOfferAwardId &&
                              componentContext.customOfferAwardId > 0
                            ) {
                              componentContext.showCreditIssueToast();
                            }

                            this.celebrationAnimationService.setCreditTopupStatus(true);
                            componentContext.stepperIndex = 1;
                            stepper.selected.completed = true;
                            stepper.selected.editable = false;
                            componentContext.isPaymentDone = false;
                            componentContext.timerOn = false;
                            componentContext.paymentHold = false;
                            stepper.next();

                            componentContext.orderSuccessEvent();
                            this.resetPaymentSubscriptions();

                            const toastCode = this.paymentService.mapPaymentToastCode(payment_status);
                            const fallback = this.paymentService.mapPaymentToastFallback(payment_status);
                            this.toastFacade.showByCode(toastCode, fallback, {
                              journey: "payment",
                              trigger: "manual_login_payment_result",
                              reason_code: this.paymentService.mapPaymentReasonCode(payment_status),
                            });

                            this.redirectAfterPaymentSuccess(componentContext);
                            let token = localStorage.getItem("fbToken");
                            if (token) {
                              this.manualLoginPrivate(token, componentContext)
                                .then(() => {
                                  this.paymentService
                                    .updatePaymentReqAndRes(
                                      componentContext.purchaseDetail$.value,
                                      componentContext.payment_status,
                                    )
                                    .subscribe((sqlAPICall) => {
                                      componentContext.sqlAPICallResponse = sqlAPICall;
                                      if (componentContext.sqlAPICallResponse?.response?.encrypt_syndicate_id) {
                                        this.paymentService.cashPurchaseAddCardSyndicateId$.next(
                                          componentContext.sqlAPICallResponse?.response?.encrypt_syndicate_id,
                                        );
                                      }
                                    });
                                  return;
                                })
                                .catch((error) => {
                                  return error;
                                });
                            }
                            return;
                          }

                          // ❌ Payment failed/cancelled
                          const toastCode = this.paymentService.mapPaymentToastCode(payment_status);
                          const fallback = this.paymentService.mapPaymentToastFallback(payment_status);
                          this.toastFacade.showByCode(toastCode, fallback, {
                            journey: "payment",
                            trigger: "manual_login_payment_result",
                            reason_code: this.paymentService.mapPaymentReasonCode(payment_status),
                          });

                          componentContext.timerOn = false;
                          componentContext.isPaymentDone = false;
                          componentContext.paymentHold = false;
                          componentContext.paymentReceived = false;
                          componentContext.paymentReceivedFromCP = false;
                          this.resetPaymentSubscriptions();

                          // redirect to previous page as user has cancelled payment
                          this.redirectToPreviousPage();
                        });
                      },
                      (err) => {
                        componentContext.isPaymentDone = false;
                        componentContext.paymentReceived = false;
                        componentContext.paymentReceivedFromCP = false;
                        componentContext.paymentHold = false;
                        this.resetPaymentSubscriptions();
                        return err;
                      },
                    );
                } catch (e) {
                  componentContext.paymentReceived = false;
                  componentContext.paymentReceivedFromCP = false;
                  componentContext.paymentHold = false;
                  this.resetPaymentSubscriptions();
                  return e;
                }
              },
              error: (err) => {
                this.catchService.logError("Error getting UID in afterNewCardPaymentDone:", err);
                componentContext.paymentReceived = false;
                componentContext.paymentReceivedFromCP = false;
                componentContext.paymentHold = false;
                this.resetPaymentSubscriptions();
              },
            });
        }, 1000);
      }
    } catch (e) {
      return e;
    }
  }

  /**
   * Load payment and update status
   */
  load(
    e: Data,
    stepper: MatStepper,
    componentContext: AddCardComponent,
    afterCardPaymentDoneCallback: () => void,
  ): void {
    localStorage.setItem("payType", "add card");
    componentContext.stepperIframe = stepper;
    componentContext.count++;
    if (componentContext.count > 1) {
      componentContext.isPaymentDone = true;
    }
    this.afterNewCardPaymentDoneUpdated(componentContext.stepperIframe, componentContext);
    afterCardPaymentDoneCallback();
  }

  /**
   * Handle checkout payment failure flow
   */
  checkoutPaymentFailedFlow(componentContext: AddCardComponent): void {
    this.loadingService.dismiss();
    this.resetPaymentSubscriptions();
    componentContext.getCheckOutComResponseSubscription?.unsubscribe();
    componentContext.generateCheckoutTokenSubscription?.unsubscribe();
    componentContext.paymentFailRedirectionStatus = true;
    this.voucherService.updateDiscountVoucherHideStatus(componentContext.selectedDiscountVoucherData?.id, false);
    componentContext.addCardPaymentStatus = false;
    componentContext.cardTokenizedCalled = false;
    componentContext.isCheckoutCom = false;
    componentContext.timerOn = false;
    componentContext.addNewCardInProgress = false;
    componentContext.proceed = true;
    componentContext.isPaymentDone = false;
    componentContext.isCheckoutComform = true;
    componentContext.redirectlink = null;
    componentContext.timerOn = false;
    this.authService.getConstantsMsg("1093").then((translatedMsg) => {
      const defaultMsg = {
        message: {
          line_1: "There is an issue in processing payment",
          line_2: "",
        },
        style: {
          icon: "tick circle error.svg",
          type: "Error",
        },
      };
      this.authService.showToastMessage(translatedMsg, defaultMsg);
    });

    this.redirectToPreviousPage();
  }

  /**
   * Handle manual login after payment
   */
  public manualLoginPrivate(token: string, componentContext: AddCardComponent): Promise<Data> {
    return new Promise((resolve, reject) => {
      componentContext.tokenValidaterSub = this.authService.getV2CloudFunction("tokenvalidater", { token }).subscribe({
        next: async (res) => {
          const { status, token, token_refresh } = res;
          if (status && token) {
            if (token_refresh) {
              localStorage.setItem(componentContext.fbTokenKey, token);
            }
            this.angularFireAuth
              .signInWithCustomToken(token)
              .then(() => {
                this.angularFireAuth.currentUser.then((user) => {
                  if (user) {
                    resolve(user);
                  } else {
                    const defaultMsg = {
                      message: {
                        line_1: "Something went wrong!",
                        line_2: "",
                      },
                      style: {
                        icon: "tick circle error.svg",
                        type: "Error",
                      },
                    };
                    this.authService.showToastMessage({}, defaultMsg);
                    reject(new Error("User is null."));
                  }
                });
              })
              .catch((error) => {
                const logData = {
                  creation_date: new Date(),
                  local_fb_token: localStorage.getItem(componentContext.fbTokenKey) || "Missing Token",
                  error,
                  page: "add-card.component.ts",
                  userId: localStorage.getItem(componentContext.userIdKey) || "Missing User ID",
                };
                const path_id = "hbqZtgNd5muQnA8eI3xe";
                const doc_data = logData;
                const method = "create";

                this.authService
                  .getV2CloudFunction("updatefirestoredoc", {
                    path_id,
                    doc_data,
                    method,
                  })
                  .subscribe({
                    error: (error) => {
                      this.catchService.logError("Error updating manualLoginPayment list:", error);
                      return false;
                    },
                  });
                reject(error);
              });
          }
        },
        error: (error) => {
          this.catchService.logError("Error in token validation:", error);
          reject(error);
        },
      });
    });
  }

  /**
   * Reset payment subscriptions
   */
  resetPaymentSubscriptions(): void {
    this.unsubscribeStatusFromCP$?.next();
    this.unsubscribeStatusFromCP$?.complete();
    this.unsubscribeStatusFromPayment$?.next();
    this.unsubscribeStatusFromPayment$?.complete();
  }

  /**
   * Redirect to previous page
   */
  redirectToPreviousPage(): void {
    this.storeService
      ?.getPreviousPageFromServer()
      ?.pipe(take(1))
      ?.subscribe({
        next: (value) => {
          if (value) {
            this.router.navigateByUrl(`${value}`);
          } else {
            this.router.navigateByUrl("/mainTabs/tabs/store");
          }
        },
        error: (error) => {
          this.router.navigateByUrl("/mainTabs/tabs/store");
          return error;
        },
      });
  }

  private redirectAfterPaymentSuccess(componentContext: AddCardComponent, encrypt_syndicate_id?: string): void {
    const purchaseDetail = componentContext.purchaseDetail$?.value;
    const isTopUp = purchaseDetail?.isTopUp || purchaseDetail?.page === "topUp";

    if (isTopUp) {
      return;
    }

    this.voucherService.setSyndicatePending().pipe(take(1)).subscribe();
    this.router.navigate(["mainTabs/tabs/syndicate"], { state: { encrypt_syndicate_id } });
  }

  initPaymentDocument(ctx: AddCardComponent): void {
    try {
      if (!ctx.paymentHold) {
        const currentUrl = this.router.url;
        localStorage.setItem("currentRoute", currentUrl);

        // ✅ Use commonService.getUid() instead of localStorage
        this.commonService
          .getUid("ngOnInit")
          .pipe(take(1))
          .subscribe((uid) => {
            if (!uid) return;

            this.catchService.isOnline$
              .pipe(
                switchMap((isOnline) => {
                  if (!isOnline) return from(Promise.resolve(null));

                  const paymentDocRef = doc(this.firestore, `users/${uid}/payment/${ctx.pathString}`);

                  return from(getDoc(paymentDocRef));
                }),
                this.catchService.retryWhen,
                this.catchService.catchOfflineError,
                shareReplay(1),
              )
              .subscribe(
                async (snapshot: DocumentSnapshot<DocumentData>) => {
                  const data = snapshot.data() as {
                    payment_response_checkout?: boolean;
                    checkout_response?: { paymentWidgetUrl?: string };
                  };

                  if (data) {
                    if (data.payment_response_checkout) {
                      this.authService.getConstantsMsg("1052").then((translatedMsg) => {
                        const defaultMsg = {
                          message: {
                            line_1: "Payment session has expired, please try again!",
                            line_2: "",
                          },
                          style: {
                            icon: "tick circle error.svg",
                            type: "Error",
                          },
                        };
                        this.authService.showToastMessage(translatedMsg, defaultMsg);
                      });
                      this.router.navigateByUrl("/mainTabs/tabs/home");
                    } else {
                      ctx.currentNdc = ctx.currentNdcSignal();
                      try {
                        await ctx.unloadPaymentWidget();
                      } catch (error) {
                        this.catchService.logError("Error unloading payment widget:", error);
                        return;
                      }
                      const paymentWidgetUrl = data.checkout_response?.paymentWidgetUrl;
                      if (!paymentWidgetUrl) {
                        this.catchService.logError(
                          "checkout-payment-service: paymentWidgetUrl is missing in Firestore checkout_response — aborting script load",
                          data,
                        );
                        ctx.errorMessage = "Payment session could not be loaded. Please try again.";
                        ctx.showCheckOutComSpinner = false;
                        return;
                      }
                      this.utilityService.loadScriptForPayment([paymentWidgetUrl]);
                      this.utilityService.checkIframeInputFocus();
                    }
                  } else {
                    this.paymentService?.checkOutComPayload$?.subscribe((reqData) => {
                      ctx.checkOutComData = reqData;
                      this.paymentService?.setcheckOutComDocId({ docId: String(ctx.pathString) });
                      ctx.isCheckoutCom = true;
                      ctx.isSecureTrading = false;
                      ctx.isPaymill = false;
                    });
                  }
                },
                (err) => {
                  this.catchService.logError("Error fetching payment document:", err);
                  throw err;
                },
              );
          });
      }
    } catch (e) {
      this.catchService.logError("Error handling payment logic:", e);
      return e;
    }
  }
  initSyndicateRequest(ctx: AddCardComponent): void {
    try {
      // ✅ Use commonService.getUid() here too
      this.commonService
        .getUid("ngOnInit")
        .pipe(take(1))
        .subscribe((uid) => {
          if (!uid || !ctx.pathString) return;

          this.catchService.isOnline$
            .pipe(
              switchMap((isOnline) => {
                if (!isOnline) return from(Promise.resolve(null));

                const syndicateDocRef = doc(this.firestore, `users/${uid}/syndicate_request/${ctx.pathString}`);

                return from(getDoc(syndicateDocRef));
              }),
              this.catchService.retryWhen,
              this.catchService.catchOfflineError,
              shareReplay(1),
            )
            .subscribe(
              (snapshot: DocumentSnapshot<DocumentData>) => {
                let purchaseDetail = snapshot.data() as {
                  add_on?: Data[];
                  offers?: { checkout_description?: string; ongoing_flag?: boolean };
                  upsell_ID?: string;
                  upsell_amt?: number;
                  payment?: string;
                  isTopUp?: boolean;
                  page?: string;
                  discount_available?: boolean;
                };
                purchaseDetail = purchaseDetail ?? ctx.storePaymentData;

                if (purchaseDetail) {
                  purchaseDetail.add_on = purchaseDetail.add_on || [];
                  ctx.purchaseDetail$.next(purchaseDetail);

                  const offerCheckoutDescription = ctx.purchaseDetail$.value?.offers?.checkout_description;
                  this.authService.currencyNodeDvData = ctx.purchaseDetail$.value?.offers;
                  if (offerCheckoutDescription) {
                    this.authService
                      .getDVFromString(offerCheckoutDescription)
                      .then((dvString: string) => {
                        ctx.checkout_description = dvString;
                      })
                      .catch((error) => {
                        this.catchService.logError("Error fetching checkout description:", error);
                      });
                  }

                  const upsellID = purchaseDetail.upsell_ID;
                  const paymentID = purchaseDetail.payment;
                  const selectedUpsell = purchaseDetail.add_on || [];
                  ctx.updatedPrizeValue = purchaseDetail.upsell_amt || 0;

                  if (upsellID && paymentID) {
                    ctx.getUpsellData(upsellID, selectedUpsell);
                  }
                  ctx.receivedData = purchaseDetail;
                  ctx.purchaseDetail$.next(purchaseDetail);

                  ctx.discountVouchersStatus = !purchaseDetail.offers?.ongoing_flag;
                  const isValidPage = [ctx.storeString, "offerofday"].includes(purchaseDetail.page?.toLowerCase());
                  if (
                    ctx.discountVouchersStatus &&
                    !purchaseDetail.isTopUp &&
                    isValidPage &&
                    purchaseDetail?.discount_available
                  ) {
                    ctx.fetchDiscountVouchersData();
                  }
                }
              },
              (err) => {
                this.catchService.logError("Error fetching syndicate request document:", err);
                throw err;
              },
            );
        });
    } catch (e) {
      this.catchService.logError("Error handling syndicate request logic:", e);
      return e;
    }
  }

  loadCards(ctx: AddCardComponent) {
    const loadCardSub = this.walletService?.paymentDetails$?.subscribe((data) => {
      ctx.initilizEvent(data);
      ctx.storedCards = data;
      if (ctx.storedCards?.length > 0) {
        let withPriority = ctx.storedCards.filter((card) => card.hasOwnProperty("priority"));
        let withoutPriority = ctx.storedCards.filter((card) => !card.hasOwnProperty("priority"));
        withPriority.sort((a, b) => b.priority - a.priority);
        ctx.storedCards = [...withPriority, ...withoutPriority];
        const latestCard = ctx.storedCards.reduce(
          (latest, card) => {
            const cardDate = new Date(card.date_created);
            return cardDate > latest.date_created ? { ...card, date_created: cardDate } : latest;
          },
          { date_created: new Date(0) },
        ); // Start with an object having the earliest possible date

        ctx.latestCardData = latestCard;
        if (!ctx.storedCards.some((card) => card?.isChecked === true)) {
          ctx.storedCards[0].isChecked = true;
        }
      }
      if (ctx.storedCards.length > 2) {
        ctx.showAll = false;
      }
    });
    ctx.sub?.add(loadCardSub);
  }
}
